"use client";

import React, { useEffect, memo } from "react";
import { Settings2, RefreshCw, Mic, Volume2 } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/_module/components/ui/dropdown-menu";
import { Slider } from "@/app/_module/components/ui/slider";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/app/_module/components/ui/tooltip";

interface AudioDevice {
  deviceId: string;
  label: string;
  kind: 'audioinput' | 'audiooutput';
}

interface AudioDeviceSelectorProps {
  audioInputDevices: AudioDevice[];
  audioOutputDevices: AudioDevice[];
  selectedInputDeviceId: string | null;
  selectedOutputDeviceId: string | null;
  onAudioInputChange: (deviceId: string) => void;
  onAudioOutputChange: (deviceId: string) => void;
  onRefreshDevices: () => void;
  microphoneAudioLevel?: number;
  isHardwareMuted?: boolean;
  isMuted?: boolean;
  compact?: boolean;
  // Speaker volume control
  speakerVolume?: number; // 0-200, 100 = normal
  onSpeakerVolumeChange?: (volume: number) => void;
  // Mic level monitoring — start/stop on dropdown open/close
  onStartAudioMonitoring?: () => void;
  onStopAudioMonitoring?: () => void;
}

export const AudioDeviceSelector = memo(function AudioDeviceSelector({
  audioInputDevices,
  audioOutputDevices,
  selectedInputDeviceId,
  selectedOutputDeviceId,
  onAudioInputChange,
  onAudioOutputChange,
  onRefreshDevices,
  microphoneAudioLevel = 0,
  isHardwareMuted = false,
  isMuted = false,
  compact = false,
  speakerVolume = 100,
  onSpeakerVolumeChange,
  onStartAudioMonitoring,
  onStopAudioMonitoring,
}: AudioDeviceSelectorProps) {
  // Refresh devices on mount
  useEffect(() => {
    if (audioInputDevices.length === 0) {
      onRefreshDevices();
    }
  }, []);

  const hasDevices = audioInputDevices.length > 0 || audioOutputDevices.length > 0;

  if (!hasDevices) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-gray-500"
              onClick={onRefreshDevices}
            >
              <Settings2 className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Audio settings</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {/* Device Selector Dropdown */}
      <DropdownMenu onOpenChange={(open) => {
        if (open) onStartAudioMonitoring?.();
        else onStopAudioMonitoring?.();
      }}>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className={`${compact ? 'h-7 px-2' : 'h-6 px-2'} text-blue-700 hover:bg-blue-100`}
                >
                  <Settings2 className={compact ? 'h-4 w-4' : 'h-3.5 w-3.5'} />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p>Audio settings</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <DropdownMenuContent align="end" className="w-72 p-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-500">Audio Devices</span>
            <Button
              size="sm"
              variant="ghost"
              className="h-5 px-1"
              onClick={onRefreshDevices}
              title="Refresh devices"
            >
              <RefreshCw className="h-3 w-3" />
            </Button>
          </div>

          {/* Microphone Selection */}
          {audioInputDevices.length > 0 && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1">
                  <Mic className="h-3 w-3 text-gray-500" />
                  <span className="text-xs font-medium text-gray-600">Microphone</span>
                </div>
                {/* Audio Level Indicator */}
                {!isMuted && (
                  <div className="flex items-center gap-1" title={`Mic level: ${microphoneAudioLevel}%`}>
                    <div className="flex gap-0.5 h-3 items-end">
                      {[1, 2, 3, 4, 5].map((bar) => (
                        <div
                          key={bar}
                          className={`w-1.5 rounded-sm transition-all ${
                            microphoneAudioLevel >= bar * 20
                              ? isHardwareMuted ? 'bg-red-400' : 'bg-green-500'
                              : 'bg-gray-300'
                          }`}
                          style={{ height: `${bar * 3}px` }}
                        />
                      ))}
                    </div>
                    <span className="text-[10px] text-gray-400 ml-1">{microphoneAudioLevel}%</span>
                  </div>
                )}
              </div>
              {audioInputDevices.map((device) => (
                <DropdownMenuItem
                  key={device.deviceId}
                  onClick={() => onAudioInputChange(device.deviceId)}
                  className={`text-xs rounded cursor-pointer ${
                    selectedInputDeviceId === device.deviceId
                      ? 'bg-blue-100 text-blue-800'
                      : ''
                  }`}
                >
                  <span className="truncate flex-1">{device.label}</span>
                  {selectedInputDeviceId === device.deviceId && (
                    <span className="ml-2 text-blue-600">✓</span>
                  )}
                </DropdownMenuItem>
              ))}
            </div>
          )}

          {/* Speaker Selection */}
          {audioOutputDevices.length > 0 && (
            <div>
              <div className="flex items-center gap-1 mb-1">
                <Volume2 className="h-3 w-3 text-gray-500" />
                <span className="text-xs font-medium text-gray-600">Speaker / Headphones</span>
              </div>
              {audioOutputDevices.map((device) => (
                <DropdownMenuItem
                  key={device.deviceId}
                  onClick={() => onAudioOutputChange(device.deviceId)}
                  className={`text-xs rounded cursor-pointer ${
                    selectedOutputDeviceId === device.deviceId
                      ? 'bg-blue-100 text-blue-800'
                      : ''
                  }`}
                >
                  <span className="truncate flex-1">{device.label}</span>
                  {selectedOutputDeviceId === device.deviceId && (
                    <span className="ml-2 text-blue-600">✓</span>
                  )}
                </DropdownMenuItem>
              ))}
              {/* Speaker Volume Slider */}
              {onSpeakerVolumeChange && (
                <div className="mt-2 px-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500">Volume</span>
                    <span className="text-xs text-gray-600 font-medium">{speakerVolume}%</span>
                  </div>
                  <Slider
                    value={[speakerVolume]}
                    onValueChange={(value) => onSpeakerVolumeChange(value[0])}
                    min={0}
                    max={200}
                    step={10}
                    className="w-full"
                  />
                  <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                    <span>0%</span>
                    <span>100%</span>
                    <span>200%</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Hardware Mute Warning */}
          {isHardwareMuted && !isMuted && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
              <strong>Warning:</strong> Microphone muted by headset button
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
});
